import type {
  SessionInfo, AuthConfig, AccessList, LeadDistFilters, LeadDistSummary, LeadDistFunnel,
  LeadDistTransitions, LeadDistFilterOptions, LeadDistDrilldown, LeadDistReport, RvaDetail, SourceMeta,
  AdminUsers, SmScope, ImpersonationTarget, Role,
} from "@analytics/shared";

/** All non-GET calls carry this header; cross-site pages can't set a custom header without a
 *  CORS preflight the API denies in prod — a lightweight CSRF guard atop the sameSite cookie. */
const CSRF = { "x-portal-csrf": "1" } as const;
const writeInit = (method: string, body?: unknown): RequestInit => ({
  method, headers: { ...CSRF }, ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});
const enc = (email: string) => encodeURIComponent(email);

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const { headers: initHeaders, ...rest } = init ?? {};
  const res = await fetch("/api/v1" + path, {
    credentials: "include",
    ...rest,
    // merge, so a caller's headers (e.g. the CSRF header on writes) don't drop content-type
    headers: { "content-type": "application/json", ...(initHeaders as Record<string, string> | undefined) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, (body as { error?: string }).error || res.statusText, body);
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body: unknown) {
    super(message);
  }
}

/** Serialize lead-distribution filters into a query string. */
function qs(f: LeadDistFilters): string {
  const p = new URLSearchParams();
  if (f.from != null) p.set("from", String(f.from));
  if (f.to != null) p.set("to", String(f.to));
  // multi-select filters: repeat the param once per selected value
  for (const v of f.groupIds ?? []) p.append("groupId", String(v));
  for (const v of f.flowIds ?? []) p.append("flowId", String(v));
  for (const v of f.projects ?? []) p.append("project", v);
  for (const v of f.rvas ?? []) p.append("rva", v);
  for (const v of f.jobIds ?? []) p.append("jobId", v);
  if (f.careGroup) p.set("careGroup", f.careGroup);
  if (f.careStatus) p.set("careStatus", f.careStatus);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Full-page nav to begin Google OIDC (a redirect, not a fetch). */
export const GOOGLE_START_URL = "/api/v1/auth/google/start";

const LD = "/lead-distribution";

export const api = {
  authConfig: () => req<AuthConfig>("/auth/config"),
  session: () => req<SessionInfo>("/session"),
  access: () => req<AccessList>("/access"),
  devLogin: (email: string, name: string) =>
    req<SessionInfo>("/auth/dev-login", { method: "POST", body: JSON.stringify({ email, name }) }),
  logout: () => req<{ ok: boolean }>("/auth/logout", { method: "POST" }),

  leadDistSource: () => req<SourceMeta>(`${LD}/source`),
  leadDistSummary: (f: LeadDistFilters) => req<LeadDistSummary>(`${LD}/summary${qs(f)}`),
  leadDistReport: (f: LeadDistFilters) => req<LeadDistReport>(`${LD}/report${qs(f)}`),
  leadDistRvaDetail: (f: LeadDistFilters, page = 0, pageSize = 20) =>
    req<RvaDetail>(`${LD}/rva-detail${qs(f)}${qs(f) ? "&" : "?"}page=${page}&pageSize=${pageSize}`),
  leadDistLifecycleFunnel: (f: LeadDistFilters) => req<LeadDistFunnel>(`${LD}/lifecycle-funnel${qs(f)}`),
  leadDistCrmFunnel: (f: LeadDistFilters) => req<LeadDistFunnel>(`${LD}/crm-funnel${qs(f)}`),
  leadDistTransitions: (f: LeadDistFilters) => req<LeadDistTransitions>(`${LD}/transitions${qs(f)}`),
  leadDistFilters: () => req<LeadDistFilterOptions>(`${LD}/filters`),
  leadDistDrilldown: (f: LeadDistFilters, limit = 100) => req<LeadDistDrilldown>(`${LD}/drilldown${qs(f)}${qs(f) ? "&" : "?"}limit=${limit}`),

  // ── admin: roles + scopes (config DB) ──
  adminUsers: () => req<AdminUsers>("/admin/users"),
  setUserRole: (email: string, role: Role) =>
    req<{ ok: boolean }>(`/admin/users/${enc(email)}/role`, writeInit("PUT", { role })),
  // adding a user = setting a role on a new @rever.vn email (grants sign-in)
  addUser: (email: string, role: Role) =>
    req<{ ok: boolean }>(`/admin/users/${enc(email)}/role`, writeInit("PUT", { role })),
  removeUser: (email: string) =>
    req<{ ok: boolean }>(`/admin/users/${enc(email)}`, writeInit("DELETE")),
  getScope: (email: string) => req<{ email: string; scope: SmScope }>(`/admin/scopes/${enc(email)}`),
  setScope: (email: string, scope: SmScope) =>
    req<{ ok: boolean }>(`/admin/scopes/${enc(email)}`, writeInit("PUT", scope)),

  // ── impersonation ──
  impersonateTargets: () => req<ImpersonationTarget[]>("/impersonate/targets"),
  impersonate: (email: string) => req<SessionInfo>("/impersonate", writeInit("POST", { email })),
  stopImpersonate: () => req<SessionInfo>("/impersonate/stop", writeInit("POST")),
};
